import { computed, ref, nextTick, ComputedRef, Ref } from 'vue';
import { ColumnOrderState, ColumnSizingState, ColumnSizingInfoState } from '@tanstack/vue-table';
import { merge } from 'lodash-es';
import { useEnvStore } from '../core/useEnvStore';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridHeaderInstance,
  GridColumnInstance,
  GridStyleOption,
  GridRowSelectionOption,
  GridDetailRowOption,
  GridColumnResizingOption,
  GridColumnAutoFitOption,
} from '../../modernGrid.types';
import {
  SCROLL_BAR_WIDTH,
  DEFAULT_COLUMN_SIZE,
  CHECKBOX_COLUMN_ID,
  EXPAND_BTN_COLUMN_ID,
  NUMBER_COLUMN_ID,
  DEFAULT_COLUMN_MIN_SIZE,
  DEFAULT_EXTRA_COLUMN_SIZE,
  COLUMN_RESIZING_HANDLE_BAR_SIZE,
  defaultColumnResizingOption,
  defaultColumnAutoFitOption,
} from '../../modernGrid.define';
import {
  existObjectValue,
  isEqualArrayValue,
  getIsDefaultColumn,
  getColumnSizeByName,
} from '../../modernGrid.utils';

export const useColumnResizing = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  gridRef,
  styleInfo,
  useNumberColumn,
  rowSelectionInfo,
  detailRowInfo,
  columnOrderState,
  hasGroupedColumns,
  tableColumns,
  envKey,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  gridRef: Ref<HTMLElement | null>;
  styleInfo: ComputedRef<GridStyleOption>;
  useNumberColumn: ComputedRef<boolean>;
  rowSelectionInfo: ComputedRef<GridRowSelectionOption>;
  detailRowInfo: ComputedRef<GridDetailRowOption>;
  columnOrderState: Ref<ColumnOrderState>;
  hasGroupedColumns: Ref<boolean>;
  tableColumns: ComputedRef<GridColumnInstance<RowDataType>[]>;
  envKey: Ref<string | undefined>;
}) => {
  const { getEnv, saveEnv } = useEnvStore(props.useDashboardStore ?? false);
  const columnResizingInfo = computed<GridColumnResizingOption>(() =>
    merge({}, defaultColumnResizingOption, props.option?.columnResizing),
  );
  const columnAutoFitInfo = computed<GridColumnAutoFitOption>(() =>
    merge({}, defaultColumnAutoFitOption, props.option?.autoFit),
  );
  const columnSizingState = ref<ColumnSizingState>({});
  let isCustomResized = false;
  const isRtl = computed<boolean>(() => table.value?.options.columnResizeDirection === 'rtl');
  const columnSizingInfoState = computed(
    () => table.value?.getState().columnSizingInfo || ({} as ColumnSizingInfoState),
  );

  const getActualScrollBarWidth = (): number => {
    const el = gridRef.value;
    if (!el) {
      return SCROLL_BAR_WIDTH;
    }
    const borderWidth = styleInfo.value.showBorder ? 2 : 0;
    return el.offsetWidth - el.clientWidth - borderWidth;
  };

  const getWrapperSizeInfo = (): {
    rectSize: number | undefined;
    minSize: number;
    withoutExceptionSize: number;
    scrollLeft: number | undefined;
  } => {
    const rectSize = gridRef.value?.getBoundingClientRect().width;
    const wrapperWidth = styleInfo.value.showBorder ? (rectSize ?? 0 - 2) : (rectSize ?? 0);
    const minSize = columnAutoFitInfo.value.columnMinSize || DEFAULT_COLUMN_MIN_SIZE;
    const scrollBarWidth = getActualScrollBarWidth();
    const withoutExceptionSize =
      wrapperWidth - scrollBarWidth - COLUMN_RESIZING_HANDLE_BAR_SIZE + 2;
    const scrollLeft = gridRef.value?.scrollLeft;

    return {
      rectSize,
      minSize,
      withoutExceptionSize,
      scrollLeft,
    };
  };

  const getDefinedColumnSize = (column: GridColumnInstance<RowDataType>): number | undefined => {
    if (column.columnDef.meta?.size) {
      return column.columnDef.meta.size;
    }
    if (column.columnDef.meta?.ignoreNameSize) {
      return undefined;
    }
    return getColumnSizeByName(column.id);
  };

  const calculateColumnWidths = (): ColumnSizingState => {
    const columns = (table.value?.getVisibleLeafColumns() ||
      []) as GridColumnInstance<RowDataType>[];
    const sizeState: ColumnSizingState = {};

    const { rectSize, minSize, withoutExceptionSize } = getWrapperSizeInfo();

    if (!rectSize) {
      return sizeState;
    }

    if (!columnAutoFitInfo.value.use) {
      columns.forEach((column) => {
        sizeState[column.id] = getDefinedColumnSize(column) || DEFAULT_COLUMN_SIZE;
      });
      return sizeState;
    }

    const columnsWithSize = columns.filter((column) => getDefinedColumnSize(column));
    const fixedWidth = columnsWithSize.reduce(
      (sum, column) => sum + (getDefinedColumnSize(column) || 0),
      0,
    );

    const columnsWithoutSize = columns.filter((column) => !getDefinedColumnSize(column));
    const flexibleColumnsCount = columnsWithoutSize.length;

    const remainingWidth = Math.max(0, withoutExceptionSize - fixedWidth);

    let autoWidth = 0;
    if (flexibleColumnsCount > 0) {
      autoWidth = Math.max(minSize, Math.floor(remainingWidth / flexibleColumnsCount));
    }

    columns.forEach((column, index) => {
      const definedSize = getDefinedColumnSize(column);

      if (definedSize) {
        sizeState[column.id] = definedSize;
      } else if (index === columns.length - 1) {
        const allocatedWidth = columns.reduce((sum, col, idx) => {
          if (idx === columns.length - 1) return sum;
          return sum + (sizeState[col.id] || autoWidth);
        }, 0);
        sizeState[column.id] = Math.max(minSize, withoutExceptionSize - allocatedWidth);
      } else {
        sizeState[column.id] = autoWidth;
      }
    });

    return sizeState;
  };

  const adjustColumnSize = (columnSizeInfo: ColumnSizingState): ColumnSizingState => {
    const updatedColumnSizeInfo = { ...columnSizeInfo };
    const columns = table.value?.getVisibleLeafColumns() || [];
    const { rectSize, minSize, withoutExceptionSize } = getWrapperSizeInfo();

    if (rectSize && columns.length) {
      const lastColumn = columns[columns.length - 1];
      const lastColMinSize =
        (lastColumn as GridColumnInstance<RowDataType>).columnDef?.minSize ?? minSize;
      let lastColumnSize = updatedColumnSizeInfo[lastColumn.id] || 0;

      // 마지막 컬럼도 일반 컬럼과 동일하게 min을 강제
      if (!getIsDefaultColumn(lastColumn.id) && lastColumnSize < lastColMinSize) {
        lastColumnSize = lastColMinSize;
        updatedColumnSizeInfo[lastColumn.id] = lastColMinSize;
      }

      const restColumnSize = columns.reduce((sum, col, idx) => {
        if (idx === columns.length - 1) return sum;

        let columnSize = updatedColumnSizeInfo[col.id] || 0;
        const effectiveMinSize =
          (col as GridColumnInstance<RowDataType>).columnDef?.minSize ?? minSize;
        if (!getIsDefaultColumn(col.id) && columnSize < effectiveMinSize) {
          columnSize = effectiveMinSize;
          updatedColumnSizeInfo[col.id] = effectiveMinSize;
        }

        return sum + columnSize;
      }, 0);
      const sumSize = restColumnSize + lastColumnSize;

      if (sumSize <= withoutExceptionSize) {
        updatedColumnSizeInfo[lastColumn.id] = Math.max(
          lastColMinSize,
          withoutExceptionSize - restColumnSize,
        );
      }
    }

    return updatedColumnSizeInfo;
  };

  const setColumnSizingState = (sizeInfo?: ColumnSizingState) => {
    const columnIds = table.value?.getVisibleFlatColumns()?.map(({ id }) => id) || [];
    const columnSizingKeys = Object.keys(columnSizingState.value);
    const envColumnSizing = getEnv(envKey.value)?.columnSizing;

    if (!isEqualArrayValue(columnIds, columnSizingKeys)) {
      isCustomResized = false;
    }

    let newState = columnSizingState.value;
    if (existObjectValue(envColumnSizing)) {
      const globalMinSize = columnAutoFitInfo.value.columnMinSize || DEFAULT_COLUMN_MIN_SIZE;
      const columns = table.value?.getVisibleLeafColumns() || [];
      columns.forEach((col) => {
        const envSize = envColumnSizing[(col as GridColumnInstance<RowDataType>).id];
        if (envSize && envSize > 0 && envSize < globalMinSize) {
          (col as GridColumnInstance<RowDataType>).columnDef.minSize = Math.min(
            envSize,
            (col as GridColumnInstance<RowDataType>).columnDef.minSize ?? globalMinSize,
          );
        }
      });
      newState = adjustColumnSize(envColumnSizing) || sizeInfo;
    } else if (sizeInfo) {
      newState = sizeInfo;
    } else if (!isCustomResized) {
      newState = calculateColumnWidths();
    }
    columnSizingState.value = newState;
    table.value?.setColumnSizing(newState);
  };

  const emitColumnSize = (sizing: ColumnSizingState) => {
    const { [CHECKBOX_COLUMN_ID]: _, [EXPAND_BTN_COLUMN_ID]: __, ...filteredSizing } = sizing;
    emit('change-column-size', { sizeInfo: filteredSizing, isCustomResized });
  };

  const resizeColumn = (
    header: GridHeaderInstance<RowDataType>,
    _tableRef: GridTableInstance<RowDataType> | undefined,
    e: MouseEvent,
  ) => {
    isCustomResized = true;
    header.getResizeHandler()(e);
  };

  /**
   * 마지막 컬럼 사이즈를 조정한 후 조정된 전체 컬럼 사이즈가 스크롤을 포함한 전체 영역보다 작을 때
   * resize-handle-mirror-clone의 left 를 조정(자동 조정이 안되는 것 같음)
   */
  const adjustResizeHandlerClonePosition = async () => {
    const adjustedSumSize = table.value?.getTotalSize() || 0;

    const { withoutExceptionSize, scrollLeft } = getWrapperSizeInfo();

    const { isResizingColumn } = columnSizingInfoState.value || {};
    const lastColumn = table.value?.getVisibleLeafColumns()?.at(-1)?.id ?? '';

    if (isResizingColumn && isResizingColumn !== lastColumn) return;

    const totalSizeWithScroll = withoutExceptionSize + Math.max(0, scrollLeft ?? 0);
    if (gridRef.value && adjustedSumSize < totalSizeWithScroll) {
      await nextTick();
      try {
        const resizeHandlerCloneEl = gridRef.value.querySelector(
          '.modern-grid-header__resize-handle-mirror-clone',
        );
        if (resizeHandlerCloneEl instanceof HTMLElement) {
          resizeHandlerCloneEl.style.left = `${adjustedSumSize}px`;
        }
      } catch {
        //
      }
    }
  };

  const updateColumnSizingState = async (
    updater: ColumnSizingState | ((state: ColumnSizingState) => ColumnSizingState),
  ) => {
    const updatedColumnSizeInfo =
      typeof updater === 'function' ? updater(columnSizingState.value) : updater;
    columnSizingState.value = adjustColumnSize(updatedColumnSizeInfo);

    await adjustResizeHandlerClonePosition();

    if (isCustomResized) {
      const envData = getEnv(envKey.value);
      await saveEnv(envKey.value, { ...envData, columnSizing: columnSizingState.value });
    }

    emitColumnSize(columnSizingState.value);
    isCustomResized = false;
  };

  const calculatePreviousColumnsSizeSum = (isResizingColumn: string | false): number => {
    if (!isResizingColumn) return 0;

    const columnOrder = hasGroupedColumns.value
      ? table.value
          ?.getHeaderGroups()
          ?.find(
            ({ depth }) =>
              depth === (tableColumns.value?.find(({ id }) => id === isResizingColumn)?.depth ?? 0),
          )
          ?.headers.map(({ column: col }) => col.id) || []
      : columnOrderState.value;
    const resizingColumnIndex = columnOrder.findIndex((columnId) => columnId === isResizingColumn);

    return columnOrder.slice(0, resizingColumnIndex).reduce((sum, columnId) => {
      if (getIsDefaultColumn(columnId)) {
        return sum;
      }

      const header = table.value?.getFlatHeaders()?.find(({ column }) => column.id === columnId);
      return sum + (header?.getSize() ?? 0);
    }, 0);
  };

  const getDefaultColumnsSize = (isResizingColumn: string | false): number => {
    if (!isResizingColumn) return 0;

    const isNumberColumnResizing = isResizingColumn === NUMBER_COLUMN_ID;
    const numberColumnSize =
      useNumberColumn.value && !isNumberColumnResizing
        ? (columnSizingState.value?.[NUMBER_COLUMN_ID] ?? DEFAULT_EXTRA_COLUMN_SIZE)
        : 0;
    const checkboxColumnSize = rowSelectionInfo.value.useCheckbox
      ? (columnSizingState.value?.[CHECKBOX_COLUMN_ID] ?? DEFAULT_EXTRA_COLUMN_SIZE)
      : 0;
    const expandColumnSize = detailRowInfo.value.use
      ? (columnSizingState.value?.[EXPAND_BTN_COLUMN_ID] ?? DEFAULT_EXTRA_COLUMN_SIZE)
      : 0;

    return numberColumnSize + (isNumberColumnResizing ? 0 : checkboxColumnSize + expandColumnSize);
  };

  const resizeHandleMirrorXPosition = computed(() => {
    if (columnResizingInfo.value.columnResizeMode !== 'onEnd') {
      return '0px';
    }

    const direction = isRtl.value ? -1 : 1;
    const { deltaOffset, startSize, isResizingColumn } = columnSizingInfoState.value || {};
    const previousColumnsSizeSum = calculatePreviousColumnsSizeSum(isResizingColumn);
    const defaultColumnsSize = getDefaultColumnsSize(isResizingColumn);
    const offset =
      (deltaOffset ?? 0) + (startSize ?? 0) + previousColumnsSizeSum + defaultColumnsSize;

    return `${direction * offset}px`;
  });

  const isVisibleResizeHandle = (header: GridHeaderInstance<RowDataType>): boolean =>
    columnResizingInfo.value.use && header.column.getCanResize();

  const resizingOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => {
    const {
      use: useResizing,
      columnMinSize,
      columnMaxSize,
      columnResizeMode,
    } = columnResizingInfo.value || {};
    return {
      defaultColumn: {
        size: DEFAULT_COLUMN_SIZE,
        minSize: columnMinSize,
        maxSize: columnMaxSize,
      },
      enableColumnResizing: useResizing,
      columnResizeMode,
      onColumnSizingChange: updateColumnSizingState,
    };
  });

  return {
    columnResizingInfo,
    columnSizingState,
    columnSizingInfoState,
    resizeHandleMirrorXPosition,
    resizingOptions,
    resizeColumn,
    isVisibleResizeHandle,
    setColumnSizingState,
    adjustColumnSize,
    getActualScrollBarWidth,
  };
};
